import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import LineChart from '@cloudscape-design/components/line-chart'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import type { SiteVisitSummary } from '@/lib/types'
import { parseGa4Date } from '@/utils/format'
import AIAnnotation from './AIAnnotation'
import { InsightBullet } from '../../../lib/types'

interface Props {
  summary: SiteVisitSummary | null
  loading: boolean
  dateRangeLabel?: string
  siteUrl?: string
  insights?: InsightBullet[]
}

export default function SiteVisitsWidget({ summary, loading, dateRangeLabel = 'all time', siteUrl, insights }: Props) {
  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
      </Box>
    )
  }
  if (!summary) {
    return (
      <Alert statusIconAriaLabel="Info" header="Connect Google">
        Connect Google Analytics 4 in client settings to see site visits.
      </Alert>
    )
  }

  const chartData = summary.dailySessions.map(d => ({
    x: parseGa4Date(d.date),
    y: d.sessions,
  }))
  const maxY = Math.max(...summary.dailySessions.map(d => d.sessions), 1)

  return (
    <SpaceBetween size="s">
      <Box variant="h3" textAlign="center">
        {summary.totalSessions.toLocaleString()} sessions ({dateRangeLabel})
      </Box>
      <LineChart
        series={[{ title: 'Sessions', type: 'line', data: chartData }]}
        xDomain={chartData.length > 0 ? [chartData[0].x, chartData[chartData.length - 1].x] : undefined}
        yDomain={[0, maxY + Math.ceil(maxY * 0.1)]}
        xScaleType="time"
        i18nStrings={{
          filterLabel: 'Filter displayed series',
          filterPlaceholder: 'Filter series',
          filterSelectedAriaLabel: '(selected)',
          legendAriaLabel: 'Legend',
          chartAriaRoleDescription: 'line chart',
          xTickFormatter: (v) => (v instanceof Date ? v.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : String(v)),
          yTickFormatter: (v) => typeof v === 'number' ? v.toLocaleString() : String(v),
        }}
        ariaLabel="Daily site visits"
        hideFilter
        height={180}
      />
      {siteUrl && (
        <Box variant="small" color="text-body-secondary" textAlign="center">
          {siteUrl}
        </Box>
      )}
      <AIAnnotation bullets={insights ?? []} />
    </SpaceBetween>
  )
}
